import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { getSetting, setSetting } from "../taskpane";

export type ThemeMode = "system" | "light" | "dark";
export type Sizing = "small" | "default" | "large";

interface ThemeContextType {
    themeMode: ThemeMode;
    setThemeMode: (mode: ThemeMode) => void;
    resolvedTheme: "light" | "dark";
    sizing: Sizing;
    setSizingOption: (sizing: Sizing) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function useTheme(): ThemeContextType {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
}

function getSystemTheme(): "light" | "dark" {
    if (typeof window !== "undefined" && window.matchMedia) {
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light";
}

function resolveTheme(mode: ThemeMode): "light" | "dark" {
    if (mode === "system") {
        return getSystemTheme();
    }
    return mode;
}

export function useSizing(): { sizing: Sizing; setSizing: (sizing: Sizing) => void } {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useSizing must be used within a ThemeProvider");
    }
    return {
        sizing: context.sizing,
        setSizing: context.setSizingOption,
    };
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
        return getSetting<ThemeMode>("themeMode", "system");
    });
    const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() => resolveTheme(themeMode));
    const [sizing, setSizing] = useState<Sizing>(() => {
        return getSetting<Sizing>("sizing", "default");
    });

    // Apply theme to document
    useEffect(() => {
        const resolved = resolveTheme(themeMode);
        setResolvedTheme(resolved);
        document.documentElement.setAttribute("data-theme", resolved);
    }, [themeMode]);

    // Listen for OS theme changes when in system mode
    useEffect(() => {
        if (themeMode !== "system") return;

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = () => {
            const resolved = resolveTheme("system");
            setResolvedTheme(resolved);
            document.documentElement.setAttribute("data-theme", resolved);
        };

        mediaQuery.addEventListener("change", handler);
        return () => mediaQuery.removeEventListener("change", handler);
    }, [themeMode]);

    const setThemeMode = (mode: ThemeMode) => {
        setThemeModeState(mode);
        setSetting("themeMode", mode);
    };

    const setSizingOption = (sizing: Sizing) => {
        setSizing(sizing);
        setSetting("sizing", sizing);
    };

    return (
        <ThemeContext.Provider value={{ themeMode, setThemeMode, resolvedTheme, sizing, setSizingOption }}>
            {children}
        </ThemeContext.Provider>
    );
}
